import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { IconAlarm, IconAlertTriangle, IconArrowLeft, IconChecklist, IconChevronDown, IconChevronUp, IconCopy, IconDots, IconLink, IconListDetails, IconPackageImport, IconRoute, IconUsers } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { PAD_TABS, PHONE_CONTS, PHONE_SEGS, ddMeta, padDetail, padItems, phoneLeft, phoneShots, type PadItem, type PhoneCont, type PhoneSeg } from './compact';
import type { ImportTab } from './types';
import d from '../docs/blDetail.module.css';
import s from './importCompact.module.css';

// Mint-Import-iPad (danh sách → chi tiết 2:1) · Mint-Import-Phone (danh sách + chi tiết). Chỉ đọc; nút ghi báo toast. money = hide.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const Chip = ({ tone, children }: { tone: string; children: ReactNode }) => <span className={`${s.chip} ${s[`t_${tone}`]}`}><i aria-hidden="true" />{children}</span>;

function Clock({ free, used }: { free: number; used: number }) {
  const m = ddMeta(free, used);
  const pct = Math.min(100, Math.round((Math.min(used, free) / free) * 100));
  const cls = m.left < 0 ? 'err' : m.left <= 2 ? 'warn' : '';
  return (
    <>
      <span className={`${s.bar} ${cls ? s[cls] : ''}`}><i style={{ width: `${pct}%` }} /></span>
      <b className={`${s.clk} ${m.left < 0 ? s.errTx : m.left <= 2 ? s.warnTx : ''}`}><IconAlarm size={13} aria-hidden="true" />{m.left < 0 ? `−${-m.left} ngày` : `${m.left} ngày`}</b>
    </>
  );
}

function Card({ id, title, heading = title, icon, actions, children }: { id: string; title: string; heading?: string; icon: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-label={title} className={d.card}>
      <div className={d.ch}>
        <span className={d.chIco} aria-hidden="true">{icon}</span>
        <h2 className={d.h2}>{heading}</h2>
        <div className={d.chAct}>
          {actions}
          <button type="button" className={d.fold} aria-label={`Mở hoặc gập ${title}`} aria-expanded={open} aria-controls={`b-${id}`} onClick={() => setOpen(!open)}>
            {open ? <IconChevronUp size={18} stroke={1.9} aria-hidden="true" /> : <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && <div id={`b-${id}`} className={d.cb}>{children}</div>}
    </section>
  );
}

export function ImportCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const lists = [useList('importShipment', { limit: 5000 }), useList('importContainerFree', { limit: 5000 }), useList('importDecl', { limit: 5000 }), useList('importReefer', { limit: 5000 }), useList('importDeposit', { limit: 5000 }), useList('importClaim', { limit: 5000 })];
  const [tab, setTab] = useState<ImportTab>('dd');
  const [seg, setSeg] = useState<PhoneSeg>('all');
  const [sel, setSel] = useState<string | null>(null);
  const forced = readState();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const failed = lists.find((x) => x.isError);
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (lists.some((x) => x.isLoading)) state = 'loading';
  else if (failed) state = failed.error instanceof DataError && failed.error.code === 'noperm' ? 'noperm' : 'error';
  const ready = state === 'ready' || state === 'noperm';
  const noperm = state === 'noperm';
  const stateProps = {
    state,
    title: 'Không có lô nào',
    desc: 'Kéo xuống để làm mới.',
    icon: <IconPackageImport size={26} stroke={1.8} />,
    action: state === 'error' ? <Button onClick={() => lists.forEach((x) => void x.refetch())}>{t('imp.retry')}</Button> : undefined,
    skeleton: <Skeleton rows={6} />,
  };

  if (phone) return <Phone seg={seg} onSeg={setSeg} sel={sel} onSel={setSel} onWrite={noWrite} stateProps={stateProps} ready={ready} noperm={noperm} />;

  // ---------- iPad ----------
  const items = padItems(tab, noperm);
  const cur = items.find((x) => x.id === sel);
  if (cur) return <PadDetail tab={tab} it={cur} onBack={() => setSel(null)} onWrite={noWrite} />;
  return (
    <Page kind="table">
      <PageHeader findPad title="Hàng nhập & container" actions={<Button variant="primary" icon={<IconAlertTriangle size={16} aria-hidden="true" />} onClick={noWrite}>{t('imp.openIssue')}</Button>} />
      <div className={s.page}>
        <div className={s.tabsRow}>
          <Segmented label="Nhóm hàng nhập" value={tab} onChange={(k) => { setTab(k as ImportTab); setSel(null); }} items={PAD_TABS.map((x) => ({ key: x.key, label: x.label, n: ready ? x.n : undefined }))} />
        </div>
        {noperm && <div role="note" className={s.note}>{t('imp.ownOnly')}</div>}
        <section aria-label="Danh sách">
          <StateView {...stateProps} state={ready && items.length === 0 ? 'empty' : ready ? 'ready' : state}>
            <ul className={s.cards}>
              {items.map((r) => (
                <li key={r.id}>
                  <button type="button" className={s.card} onClick={() => setSel(r.id)}>
                    <b className={s.k}>{r.k}</b>
                    <Chip tone={r.tone}>{r.chip}</Chip>
                    <span className={s.s}>{r.s}</span>
                    {r.clock && <Clock free={r.clock[0]} used={r.clock[1]} />}
                  </button>
                </li>
              ))}
            </ul>
          </StateView>
        </section>
      </div>
    </Page>
  );
}

function PadDetail({ tab, it, onBack, onWrite }: { tab: ImportTab; it: PadItem; onBack: () => void; onWrite: () => void }) {
  const t = useT();
  const det = padDetail(tab, it);
  const back = PAD_TABS.find((x) => x.key === tab)?.back ?? '';
  const toast = useToast();
  const copy = (v: string) => { void navigator.clipboard?.writeText(v); toast({ msg: `Đã sao chép ${v}` }); };
  const main = det.acts[0]!;
  return (
    <Page kind="table">
      <PageHeader findPad
        title={it.k}
        tabs={<Chip tone={it.tone}>{it.chip}</Chip>}
        actions={
          <>
            <button type="button" className={s.back} aria-label={`Quay lại danh sách ${back}`} title={`Quay lại danh sách ${back}`} onClick={onBack}><IconArrowLeft size={18} aria-hidden="true" /></button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={onWrite}><IconDots size={18} aria-hidden="true" /></button>
            <Button variant="primary" onClick={onWrite}>{main[0]}</Button>
          </>
        }
      />
      <div className={d.page}>
        <div className={s.dsub}>{det.kind} · {it.s}</div>
        <div className={d.grid}>
          <div className={d.col}>
            <Card id="plan" title="Kế hoạch" heading={det.stepK} icon={<IconRoute size={18} stroke={1.9} />}>
              {det.steps.map((x, i) => (
                <div key={x.k} className={d.step}>
                  <div className={d.dotCol}>
                    <span className={`${d.dot} ${x.done ? d.dotDone : x.cur ? d.dotCur : ''}`}>{i + 1}</span>
                    {i < det.steps.length - 1 && <span className={`${d.ln} ${x.done ? d.lnDone : ''}`} />}
                  </div>
                  <div className={d.stepTx}>{x.k}<div>{x.v || '—'}</div></div>
                </div>
              ))}
            </Card>
            <Card id="info" title="Thông tin" icon={<IconListDetails size={18} stroke={1.9} />}>
              <dl className={d.facts}>
                {det.pairs.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
              </dl>
            </Card>
          </div>
          <section aria-label="Tóm tắt bên phải" className={d.col}>
            <Card id="todo" title="Việc cần làm" icon={<IconChecklist size={18} stroke={1.9} />}>
              {det.alert && <div className={`${s.alert} ${det.alertTone === 'err' ? s.err : ''}`} role="note">{det.alert}</div>}
              <div className={s.btns}>
                {det.acts.map(([k, kind]) => <Button key={k} variant={kind === 'pri' ? 'primary' : 'secondary'} onClick={onWrite}>{k}</Button>)}
              </div>
            </Card>
            <Card id="main" title="Thông tin chính" icon={<IconListDetails size={18} stroke={1.9} />}>
              <div className={d.kv}>
                <span className={d.k}>{t('imp.code')}</span><span className={d.v} title={it.k}>{it.k}</span>
                <button type="button" className={d.sq} aria-label="Sao chép Mã" title="Sao chép" onClick={() => copy(it.k)}><IconCopy size={15} aria-hidden="true" /></button>
              </div>
              {it.s.split(' · ').slice(0, 3).map((v, i) => <div key={v} className={d.kv}><span className={d.k}>{['Khách', 'Chi tiết', 'Loại'][i]}</span><span className={d.v} title={v}>{v}</span></div>)}
            </Card>
            <Card id="pic" title="Người phụ trách" icon={<IconUsers size={18} stroke={1.9} />}>
              <div className={d.pic} role="listitem"><span className={d.av}>—</span><span className={d.picTx}><span>{'THUỶ'}</span><div>{t('imp.csOwner')}</div></span></div>
              <div className={d.pic} role="listitem">
                <span className={d.av}>—</span><span className={d.picTx}><span className={d.mute}>{t('imp.unassigned')}</span><div>{t('imp.arAssigned')}</div></span>
                <button type="button" className={d.give} aria-label="Giao Kế toán công nợ" onClick={onWrite}>Giao</button>
              </div>
            </Card>
            <Card id="links" title="Liên kết" icon={<IconLink size={18} stroke={1.9} />}>
              <a className={d.link} href="/ezy/tms" onClick={(e) => { e.preventDefault(); onWrite(); }}><IconRoute size={16} aria-hidden="true" /><span>{t('imp.trip')}</span><span>{t('imp.transport')}</span></a>
            </Card>
          </section>
        </div>
      </div>
    </Page>
  );
}

type SP = { state: ScreenState; title: string; desc: string; icon: ReactNode; action: ReactNode; skeleton: ReactNode };
function Phone({ seg, onSeg, sel, onSel, onWrite, stateProps, ready, noperm }: { seg: PhoneSeg; onSeg: (k: PhoneSeg) => void; sel: string | null; onSel: (k: string | null) => void; onWrite: () => void; stateProps: SP; ready: boolean; noperm: boolean }) {
  const t = useT();
  const mine = (c: PhoneCont) => !noperm || ['NDP', 'NPFruit', 'RuiTong-SC'].includes(c.cus);
  const all = PHONE_CONTS.filter(mine);
  const segOf = (c: PhoneCont) => (phoneLeft(c) < 0 ? 'over' : phoneLeft(c) <= 2 ? 'soon' : 'ok');
  const rows = all.filter((c) => seg === 'all' || segOf(c) === seg);
  const cur = PHONE_CONTS.find((c) => c.k === sel);
  if (cur) return <PhoneDetail c={cur} i={PHONE_CONTS.indexOf(cur)} onBack={() => onSel(null)} onWrite={onWrite} />;
  return (
    <Page kind="table">
      <PageHeader findPad title="Container" actions={<button type="button" className={s.more} aria-label="Mở sự cố" title="Mở sự cố" onClick={onWrite}><IconAlertTriangle size={22} aria-hidden="true" /></button>} />
      <div className={s.page}>
        <Segmented label="Hạn free time" value={seg} onChange={(k) => onSeg(k as PhoneSeg)} items={PHONE_SEGS.map((x) => ({ key: x.key, label: x.label, n: ready ? all.filter((c) => x.key === 'all' || segOf(c) === x.key).length : undefined }))} />
        {noperm && <div role="note" className={s.note}>{t('imp.ownOnly')}</div>}
        <StateView {...stateProps} state={ready && rows.length === 0 ? 'empty' : ready ? 'ready' : stateProps.state}>
          <ul className={s.cards}>
            {rows.map((c) => {
              const m = ddMeta(c.ftK, c.used);
              return (
                <li key={c.k}>
                  <div role="group" aria-label={c.k} tabIndex={0} className={s.card} onClick={() => onSel(c.k)} onKeyDown={(e) => { if (e.key === 'Enter') onSel(c.k); }}>
                    <b className={s.k}>{c.k}</b>
                    <Chip tone={m.tone}>{m.left < 0 ? 'Quá hạn' : m.chip}</Chip>
                    <span className={s.s}>{c.cus} · {c.kind} · Free hãng {c.ftC} · khách {c.ftK} ngày</span>
                    <Clock free={c.ftK} used={c.used} />
                  </div>
                </li>
              );
            })}
          </ul>
        </StateView>
      </div>
    </Page>
  );
}

function PhoneDetail({ c, i, onBack, onWrite }: { c: PhoneCont; i: number; onBack: () => void; onWrite: () => void }) {
  const t = useT();
  const m = ddMeta(c.ftK, c.used);
  const shots = phoneShots(c, i);
  return (
    <Page kind="table">
      <PageHeader findPad title={c.k} actions={<Chip tone={m.tone}>{m.left < 0 ? 'Quá hạn' : m.chip}</Chip>} />
      <div className={s.page}>
        <button type="button" className={s.back} aria-label="Quay lại" onClick={onBack}><IconArrowLeft size={22} aria-hidden="true" /></button>
        <div className={s.dsub}>{c.job} · {c.cus} · {c.car} · {c.kind}</div>
        <div className={d.card} style={{ padding: 14 }}>
          <div className={s.hrow}><IconAlarm size={16} aria-hidden="true" /><b>{m.left < 0 ? `Quá ${-m.left} ngày` : `Còn ${m.left} ngày`}</b><span className={s.mut}>/ {c.ftK} ngày</span></div>
          <span className={`${s.bar} ${m.left < 0 ? s.err : m.left <= 2 ? s.warn : ''}`} style={{ display: 'block', marginTop: 10 }}><i style={{ width: `${Math.min(100, Math.round((Math.min(c.used, c.ftK) / c.ftK) * 100))}%` }} /></span>
          <div className={s.dsub} style={{ marginTop: 8 }}>Free hãng {c.ftC} · khách {c.ftK} ngày{c.rf ? ` · cài ${c.set} · vent ${c.vent}` : ''}</div>
        </div>
        <h2 className={s.h3}>{c.rf ? 'Ảnh nhiệt độ & EIR' : 'Ảnh EIR'}</h2>
        <div className={s.shots}>
          {shots.map((x) => (
            <button key={x.k} type="button" className={`${s.shot} ${s[x.state]}`} onClick={onWrite}><b>{x.k}</b>{x.v}</button>
          ))}
        </div>
        <h2 className={s.h3}>{t('imp.milestone')}</h2>
        <div className={d.card} style={{ padding: 14 }}>
          {[['Dỡ hàng / lấy cont', '16/Oct/2026 · ePort', true], [c.kind.startsWith('DEM') ? 'Lấy khỏi cảng' : 'Trả rỗng', 'chưa', false], ['CS xác nhận số ngày', m.left < 0 ? 'chờ' : '—', false]].map(([k, v, done]) => (
            <div key={String(k)} className={d.step}><div className={d.dotCol}><span className={`${d.dot} ${done ? d.dotDone : ''}`} /></div><div className={d.stepTx}>{k}<div>{v}</div></div></div>
          ))}
        </div>
        <div className={s.two}>
          <Button variant="primary" className={s.cta} onClick={onWrite}>{t('imp.notifyCustomer')}</Button>
          <Button className={s.cta} onClick={onWrite}>{t('imp.openIssue')}</Button>
        </div>
      </div>
    </Page>
  );
}
